import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * /v2/auth session endpoints (PD-1512). Two credential channels, never mixed (PD-1542):
 *
 * - web: the refresh handle travels ONLY in the httpOnly `bh_refresh` cookie — never in
 *   a request or response body.
 * - native (mobile apps): the handle travels ONLY in request/response bodies — the
 *   device has no cookie jar worth trusting; the token lives in the Keychain.
 *
 * The broker enforces channel/session agreement, and body-channel requests are refused
 * when an Origin header is present (browsers always send one on POST) — so browser code,
 * including XSS, can neither read the cookie nor obtain a body token.
 */

const sessionKindSchema = z.enum(['provider', 'patient'])

export const authRefresh = oc
  .route({ method: 'POST', path: '/v2/auth/refresh' })
  .input(
    z
      .object({
        /** Native channel only: the body-held refresh token. Web sends nothing (cookie). */
        refreshToken: z.string().optional(),
      })
      .optional(),
  )
  .output(
    z.object({
      accessToken: z.string(),
      /** ISO instant — when the access token dies; clients schedule the next refresh off it. */
      accessTokenExpiresAt: z.string(),
      profileRef: z.string(),
      /** Same as the login grant's: a cold start restores identity through refresh (PD-1649). */
      profileDisplay: z.string().optional(),
      sessionKind: sessionKindSchema,
      /** Native channel only: the rotated refresh token — replace the stored one. Absent
       *  on the web channel by design (the cookie carries the rotation). */
      refreshToken: z.string().optional(),
    }),
  )

export const authLogout = oc
  .route({ method: 'POST', path: '/v2/auth/logout' })
  .input(
    z
      .object({
        /** Native channel only: the body-held refresh token to revoke. */
        refreshToken: z.string().optional(),
      })
      .optional(),
  )
  .output(z.object({ ok: z.literal(true) }))
